import { RouteMetadata } from './types';

export const workflowsMetadata: RouteMetadata[] = [
    {
        pattern: '/agents/:agentId/workflows/:workflowId/versions/:versionId/builder',
        section: 'workflows',
        view: 'builder',
        descriptionTemplate:
            'User is in the workflow builder for workflow {workflowId}, version {versionId}',
        capabilities: [
            'Add, remove, and configure workflow nodes',
            'Connect nodes to define execution flow',
            'Configure node parameters and inputs',
            'Save changes to the workflow version',
            'Test individual nodes',
        ],
        hints: [
            'The builder uses a visual node-based editor',
            'Nodes can be LLM calls, API requests, conditionals, loops, etc.',
            'Each node has configurable inputs and outputs',
            'Connect nodes by dragging from output to input ports',
        ],
    },
    {
        pattern: '/agents/:agentId/workflows/:workflowId/versions/:versionId/history',
        section: 'workflows',
        view: 'history',
        descriptionTemplate:
            'User is viewing execution history for workflow {workflowId}, version {versionId}',
        capabilities: [
            'View all past executions',
            'Filter executions by status or date',
            'View execution logs and events',
            'Debug failed executions',
            'Access detailed execution traces',
        ],
        hints: [
            'History shows all workflow runs',
            'Each execution shows status, duration, and timestamp',
            'Click on an execution to see detailed logs',
            'Failed executions show error information',
        ],
    },
    {
        pattern: '/agents/:agentId/workflows/:workflowId/versions/:versionId/testing/:testCaseId',
        section: 'workflows',
        view: 'testing',
        descriptionTemplate:
            'User is configuring test case {testCaseId} on the Testing tab for workflow {workflowId}, version {versionId}',
        capabilities: [
            'Configure the Persona Agent for the selected test case',
            'Configure the Judge Agent rubric for conversation and trajectory',
            'Run the selected test case',
            'Navigate back to the Testing case list',
        ],
        hints: [
            'Persona Agent defines who talks to the workflow under test',
            'Judge Agent scores conversation and trajectory with a weighted rubric',
            'Configs are mock/local until a harness API exists',
        ],
    },
    {
        pattern: '/agents/:agentId/workflows/:workflowId/versions/:versionId/testing',
        section: 'workflows',
        view: 'testing',
        descriptionTemplate:
            'User is on the Testing tab for workflow {workflowId}, version {versionId}',
        capabilities: [
            'Browse mock test cases',
            'Open a test case to configure persona and judge agents',
            'Run all tests or a single case',
        ],
        hints: [
            'Select a case to open its URL-routed config page',
            'Playground remains the place to invoke a live run',
        ],
    },
    {
        pattern:
            '/agents/:agentId/workflows/:workflowId/versions/:versionId/:mode/threads/:threadId/runs/:runId/events/:eventId',
        section: 'workflows',
        view: 'playground',
        descriptionTemplate:
            'User is viewing event {eventId} in run {runId} of thread {threadId} in {mode} mode for workflow {workflowId}, version {versionId}',
        capabilities: [
            'View detailed event information',
            'Inspect event inputs and outputs',
            'Navigate to other events in the run',
            'View event timing and duration',
        ],
        hints: [
            'Events represent individual steps in workflow execution',
            'Each event shows detailed execution data',
            'Events can be tool calls, LLM responses, or node executions',
        ],
    },
    {
        pattern:
            '/agents/:agentId/workflows/:workflowId/versions/:versionId/:mode/threads/:threadId/runs/:runId',
        section: 'workflows',
        view: 'playground',
        descriptionTemplate:
            'User is viewing run {runId} in thread {threadId} in {mode} mode for workflow {workflowId}, version {versionId}',
        capabilities: [
            'View execution trace and node outputs',
            'Inspect individual node results',
            'Re-run with different inputs',
            'Debug failed nodes',
            'View all events in the run',
        ],
        hints: [
            'The playground shows real-time execution',
            'Each node shows its input, output, and timing',
            'Failed nodes show error details',
            'Click on nodes to see detailed execution data',
        ],
    },
    {
        pattern:
            '/agents/:agentId/workflows/:workflowId/versions/:versionId/:mode/threads/:threadId',
        section: 'workflows',
        view: 'playground',
        descriptionTemplate:
            'User is viewing thread {threadId} in {mode} mode for workflow {workflowId}, version {versionId}',
        capabilities: [
            'View all runs in this thread',
            'Start a new run in this thread',
            'View thread conversation history',
            'Switch between different visualization modes',
        ],
        hints: [
            'Threads maintain conversation context across runs',
            'Each thread can have multiple runs',
            'Mode can be graph view, JSON view, or other visualizations',
        ],
    },
    {
        pattern: '/agents/:agentId/workflows/:workflowId/versions/:versionId/:mode',
        section: 'workflows',
        view: 'playground',
        descriptionTemplate:
            'User is in the playground in {mode} mode for workflow {workflowId}, version {versionId}',
        capabilities: [
            'Execute the workflow with test inputs',
            'View execution results in real-time',
            'Switch between graph and JSON modes',
            'Create new threads for testing',
            'Debug workflow execution',
        ],
        hints: [
            'The playground is for testing workflows',
            'You can provide input data and see how the workflow processes it',
            'Graph mode shows visual execution flow',
            'JSON mode shows raw data',
        ],
    },
    {
        pattern: '/agents/:agentId/workflows',
        section: 'workflows',
        view: 'list',
        descriptionTemplate: 'User is viewing the workflows list',
        capabilities: [
            'View all workflows',
            'Create new workflows',
            'Import workflows from JSON',
            'Delete workflows',
            'Search and filter workflows',
        ],
        hints: [
            'Workflows are automation sequences with multiple steps',
            'Each workflow can have multiple versions',
            'Workflows can be published or in draft state',
        ],
    },
];
